import { isRejectedWithValue, Middleware } from '@reduxjs/toolkit'
import i18n from 'i18next'

import { SILENT_AUTH_ERROR } from '@/api/http.api'
import { notify } from '@/controllers/toast-bridge'
import { readAuthFlag } from '@/store/services/local-storage.service'

/**
 * Action types whose rejections are handled inline by the feature hook
 * (inline error state + no global toast). Suppress the global notification
 * for these so the user doesn't see a floating toast that could accidentally
 * cause the Radix Dialog to close via onPointerDownOutside.
 */
const INLINE_HANDLED_ACTIONS = new Set([
  'auditLog/fetch/rejected',
  'dashboardViews/get/rejected',
  'auth/doMfaVerify/rejected',
  'auth/doMfaRegenerateBackupCodes/rejected',
  'auth/doConfirmMethodChange/rejected',
  'auth/doMfaEnable/rejected',
  'auth/doEnableEmailMfa/rejected',
  'auth/doSendEmailMfaForSetup/rejected',
  'auth/doResetPassword/rejected',
  'serviceOrders/fetch/rejected',
  'serviceOrders/fetchDevices/rejected',
  'serviceOrders/fetchTechnicians/rejected',
  'serviceOrderDetails/fetch/rejected',
  'serviceOrderDetails/fetchRejectedRequest/rejected',
  'serviceActivities/fetch/rejected',
  'serviceArchive/fetch/rejected',
  'serviceRequests/fetch/rejected',
  'serviceRequests/refresh/rejected',
  'deviceServiceRequests/fetch/rejected',
  'serviceRequests/fetchOne/rejected',
  'myOrders/fetchActive/rejected',
  'myOrders/fetchCompleted/rejected',
])

export const errorLoggingMiddleware: Middleware = () => (next) => (action) => {
  if (isRejectedWithValue(action) && !INLINE_HANDLED_ACTIONS.has((action as { type: string }).type)) {
    if (!readAuthFlag()) return next(action)
    if (action.payload === SILENT_AUTH_ERROR) return next(action)

    const errorKey = typeof action.payload === 'string' ? action.payload : 'common.genericError'
    const translatedMessage = i18n.exists(errorKey) ? i18n.t(errorKey) : i18n.t('common.genericError')
    notify.error(translatedMessage)
  }

  return next(action)
}
